import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { HasPermissionDirective } from '../../../utils/has-permission.directive';
import { ConfirmAlertComponent } from '../../shared/confirm-alert/confirm-alert.component';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { FullSpinnerService } from '../../../services/full-spinner.service';
import { lastValueFrom, Subscription } from 'rxjs';
import { ActivatedRoute, Router, ɵEmptyOutletComponent } from '@angular/router';
import { AppConstants } from '../../../utils/AppConstants';
import { License } from '../../../models/License';
import { LicenseService } from '../../../services/license.service';
import { AppEnums } from '../../../models/AppEnums';
import { AlertService } from '../../shared/alert/alert.service';
import { Account } from '../../../models/Account';
import { AccountService } from '../../../services/account.service';
import { AccountSearchCriteria } from '../../../models/searchCriteria/AccountSearchCriteria';
import { Device } from '../../../models/Device';
import { DeviceService } from '../../../services/device.service';
import { DeviceSearchCriteria } from '../../../models/searchCriteria/DeviceSearchCriteria';
@Component({
  selector: 'app-license-detail',
  standalone: true,
  imports: [
    CommonModule,
    HasPermissionDirective,
    ConfirmAlertComponent,
    FormsModule,
    ReactiveFormsModule,
],
  templateUrl: './license-detail.component.html',
  styleUrl: './license-detail.component.scss',
})
export class LicenseDetailComponent implements OnInit, OnDestroy {

  licenseTypes = AppEnums.licenseTypesTypes
  licenseModules = AppEnums.licenseModuleTypes


  paramSubscription!: Subscription;
  showConfirmationModal: boolean = false;
  confirmationMessage: string = '';

  id?: number;
  license: License = { configuration: {} };
  detail_title: string = 'Nuova licenza';

  accounts: Account[] = [];
  accountUsername: string = '';
  accountPatternValidation = '';

  devices: Device[] = [];
  deviceName: string = '';
  devicePatternValidation = '';

  constructor(
    private licenseService: LicenseService,
    private accountService: AccountService,
    private deviceService: DeviceService,
    private alertService: AlertService,
    private fullSpinnerService: FullSpinnerService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.paramSubscription = this.activatedRoute.params.subscribe(
      (parameter) => {
        this.id = parameter['id'];
        if (this.id! > 0) {
          this.fullSpinnerService.spinner('');
          this.licenseService.get(this.id!).subscribe({
            next: (res) => {
              this.license = res;
              if (!this.license.configuration) this.license.configuration = {};
              this.detail_title = res.name!;
              if (res.accountId) {
                this.getAccountById(res.accountId).then((r) => {
                  this.accountUsername = r.username!;
                  this.accountPatternValidation = '^' + this.accountUsername + '$';
                });
              }
              if (res.deviceId) {
                this.getDeviceById(res.deviceId).then((r) => {
                  this.deviceName = r.name!;
                  this.devicePatternValidation = '^' + this.deviceName + '$';
                });
              }
            },
            complete: () => {
              this.fullSpinnerService.spinner(undefined);
            },
            error: () => {
              this.fullSpinnerService.spinner(undefined);
            },
          });
        }
      }
    );
  }

  private async getAccountById(accountId: string) {
    let account$ = this.accountService.get(accountId);
    return await lastValueFrom(account$);
  }

  onAccountKeyUp(event: any) {
    //Fake Validation
    this.accountPatternValidation = '@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@';
    let value = event.target.value;
    if (value.length > 3) {
      let sc: AccountSearchCriteria = {
        username: value,
        pageNumber: 1,
        pageSize: 30,
      };
      this.accountService.find(sc).subscribe({
        next: (res) => {
          this.accounts = res.items;
        },
      });
    }
  }

  selectAccount(account: Account) {
    this.license.accountId = account.accountId!;
    this.accountUsername = account.username!;
    this.accountPatternValidation = '^' + this.accountUsername + '$';
    this.accounts = [];
  }

  private async getDeviceById(deviceId: string) {
    let device$ = this.deviceService.get(deviceId);
    return await lastValueFrom(device$);
  }

  onDeviceKeyUp(event: any) {
    //Fake Validation
    this.devicePatternValidation = '@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@';
    let value = event.target.value;
    if (value.length > 3) {
      let sc: DeviceSearchCriteria = {
        name: value,
        pageNumber: 1,
        pageSize: 30,
      };
      this.deviceService.find(sc).subscribe({
        next: (res) => {
          this.devices = res.items;
        },
      });
    }
  }

  selectDevice(device: Device) {
    this.license.deviceId = device.id!;
    this.deviceName = device.name!;
    this.devicePatternValidation = '^' + this.deviceName + '$';
    this.devices = [];
  }

  isModuleActive(moduleId: string): boolean {
    return !!this.license.configuration.activeModules?.includes(moduleId);
  }

  isExternalCameraAvailable(): boolean {
    return this.isModuleActive('SAFE_PATROL') || this.isModuleActive('CAMERA_CITY');
  }

  toggleModule(moduleId: string): void {
    if (!this.license.configuration.activeModules) this.license.configuration.activeModules = [];
    const modules = this.license.configuration.activeModules;
    const index = modules.indexOf(moduleId);
    if (index >= 0) modules.splice(index, 1);
    else modules.push(moduleId);

    if ((moduleId === 'SAFE_PATROL' || moduleId === 'CAMERA_CITY') && !this.isExternalCameraAvailable()) {
      const externalCameraIndex = modules.indexOf('USE_EXTERNAL_CAMERA');
      if (externalCameraIndex >= 0) modules.splice(externalCameraIndex, 1);
    }
  }

  onSubmit(form: NgForm): void {
    if (form.valid) {
      this.fullSpinnerService.spinner('');

      if (this.accountUsername == '') this.license.accountId = undefined;
      if (this.deviceName == '') this.license.deviceId = undefined;

      this.licenseService.save(this.license).subscribe({
        next: (_) => {
          this.alertService.addErrors(
            AppConstants.titleSuccessMessage,
            [AppConstants.successMessage],
            true
          );
          this.back();
        },
        complete: () => {
          this.fullSpinnerService.spinner(undefined);
        },
        error: () => {
          this.alertService.addErrors(
            AppConstants.titleErrorMessage,
            [AppConstants.errorMessage],
            true
          );
          this.fullSpinnerService.spinner(undefined);
        },
      });
    }
  }

  back() {
    this.router.navigate(['licenses']);
  }

  confirmDelete() {
    this.showConfirmationModal = true;
    this.confirmationMessage =
      'Sicuro di voler eliminare la licensa ' + this.license.name + ' ?';
  }

  cancelDelete() {
    this.showConfirmationModal = false;
  }

  delete() {
    this.fullSpinnerService.spinner('');
    this.licenseService.delete(this.license.id!).subscribe({
      next: (res) => {
        this.alertService.addErrors(
          AppConstants.titleDeleteSuccessMessage,
          [AppConstants.successMessage],
          true
        );
       this.back();
      },
      complete: () => {
        this.fullSpinnerService.spinner(undefined);
      },
      error: () => {
        this.alertService.addErrors(
          AppConstants.titleErrorMessage,
          [AppConstants.errorMessage],
          true
        );
        this.fullSpinnerService.spinner(undefined);
      },
    });
  }

  ngOnDestroy(): void {
    this.paramSubscription.unsubscribe();
  }
}
